import * as React from 'react';
import gql from 'graphql-tag';
import AuthorizedQuery from './AuthorizedQuery';
import { QueryResult } from 'react-apollo';

const NDVI_QUERY = gql(`
  query NdviQuery($fieldId: ID!, $sceneId: ID!) {
    field(id: $fieldId) {
      id
      name
      area
      length
      geometry {
        type
        coordinates
      }
      raster: ndviRaster(sceneId: $sceneId) {
        id
        rasterUrl
        bins {
          max
          min
          color
        }
      }
    }
  }
`);

const TRUECOLOR_QUERY = gql(`
  query TrueColorQuery($fieldId: ID!, $sceneId: ID!) {
    field(id: $fieldId) {
      id
      name
      area
      length
      geometry {
        type
        coordinates
      }
      raster: trueColorRaster(sceneId: $sceneId) {
        id
        rasterUrl
      }
    }
  }
`);

const FALSECOLOR_QUERY = gql(`
  query FalseColorQuery($fieldId: ID!, $sceneId: ID!) {
    field(id: $fieldId) {
      id
      name
      area
      length
      geometry {
        type
        coordinates
      }
      raster: falseColorRaster(sceneId: $sceneId) {
        id
        rasterUrl
      }
    }
  }
`);

type ChildreFn = (
  rasterUrl: string | null,
  geometry: PolygonGeometry | null,
  bins: NdviRasterBins | null,
  loading: boolean
) => React.ReactNode;

interface Props {
  fieldId: string | null;
  rasterMode: RasterMode | null;
  scene: SceneDocument | null;
  children: ChildreFn;
}

function renderChildren(
  props: Props,
  result: QueryResult
): React.ReactNode {
  if (shouldSkip(props)) {
    return props.children(null, null, null, false);
  }

  if (result.loading) {
    return props.children(null, null, null, true);
  }

  if (!result.data || !result.data.field) {
    return props.children(null, null, null, false);
  }

  const { field } = result.data;
  return props.children(field.raster.rasterUrl, field.geometry, field.raster.bins, false);
}

const getQuery = (mode: RasterMode | null) => {
  if (!mode) {
    return null;
  }

  switch (mode) {
    case 'ndvi':
      return NDVI_QUERY;
    case 'truecolor':
      return TRUECOLOR_QUERY;
    case 'falsecolor':
      return FALSECOLOR_QUERY;
    default:
      return null;
  }
};

function buildVariables(props: Props) {
  return {
    fieldId: props.fieldId,
    sceneId: props.scene && props.scene.sceneId
  };
}

function shouldSkip(props: Props) {
  return !props.scene || !props.fieldId || !props.rasterMode;
}

const RasterProvider: React.StatelessComponent<Props> = (props: Props) => (
  <AuthorizedQuery
    query={getQuery(props.rasterMode)}
    skip={shouldSkip(props)}
    variables={buildVariables(props)}
  >
    {result => renderChildren(props, result)}
  </AuthorizedQuery>
);

export default RasterProvider;



// WEBPACK FOOTER //
// ./src/providers/RasterProvider.tsx